import React from 'react';

/**
 * Template 1: Bold Text + Face
 * Giant bold sans-serif text (2-4 words) top/center-left,
 * person cutout on the right side, solid or gradient bg.
 * Optional highlight box behind a key word.
 *
 * Inspired by: "FOLLOW THIS", "RICH BUYER MESSAGING", "SHOW RATE CHECKLIST"
 */
export default function JH_BoldTextFace({
  headline = 'FOLLOW THIS',
  personImage,
  bgColor = '#F0F4F8',
  accentColor = '#FFD700',
  highlightWords = '',
}) {
  const isLightBg = isLightColor(bgColor);
  const textColor = isLightBg ? '#111111' : '#FFFFFF';
  const shadowStyle = isLightBg ? 'none' : '0 2px 12px rgba(0,0,0,0.5)';

  // Split headline to apply highlight to specific words
  const words = headline.split(' ');
  const highlightSet = new Set(highlightWords.toLowerCase().split(',').map(w => w.trim()).filter(Boolean));

  return (
    <div style={{
      width: 1280, height: 720, position: 'relative', overflow: 'hidden',
      background: bgColor, fontFamily: "'Inter', sans-serif",
    }}>
      {/* Subtle grid texture */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.06,
        backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />

      {/* Headline */}
      <div style={{
        position: 'absolute', top: 40, left: 60, width: 700, zIndex: 2,
      }}>
        <h1 style={{
          margin: 0, fontSize: 108, fontWeight: 900, lineHeight: 1.0,
          color: textColor, textShadow: shadowStyle, letterSpacing: '-2px',
          textTransform: 'uppercase',
        }}>
          {words.map((word, i) => {
            const shouldHighlight = highlightSet.has(word.toLowerCase());
            return (
              <span key={i}>
                {shouldHighlight ? (
                  <span style={{
                    background: accentColor, padding: '2px 16px', display: 'inline-block',
                    color: isLightColor(accentColor) ? '#111' : '#fff',
                  }}>{word}</span>
                ) : word}
                {i < words.length - 1 ? ' ' : ''}
              </span>
            );
          })}
        </h1>
      </div>

      {/* Person image */}
      {personImage && (
        <div style={{
          position: 'absolute', right: 0, bottom: 0, width: 620, height: 720,
          zIndex: 1,
        }}>
          <img
            src={personImage}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
          />
        </div>
      )}

      {/* Placeholder silhouette when no image */}
      {!personImage && (
        <div style={{
          position: 'absolute', right: 60, bottom: 0, width: 500, height: 650,
          background: `linear-gradient(135deg, ${isLightBg ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'} 0%, transparent 100%)`,
          borderRadius: '12px 12px 0 0', zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 48, color: isLightBg ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)',
        }}>
          PHOTO
        </div>
      )}
    </div>
  );
}

function isLightColor(color) {
  const hex = (color || '#000').replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const toL = c => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toL(r) + 0.7152 * toL(g) + 0.0722 * toL(b) > 0.35;
}
